import React, { useEffect, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import TenantActions from '@Actions/tenantActions';
import UnicommerceIntegrationActions from '@Actions/integrations/unicommerceIntegrationActions';
import Helpers from '@Apis/helpers';
import {
  Table, Popconfirm, Checkbox, Select, Drawer,
  Button,
} from 'antd';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import {
  DeleteOutlined, PlusCircleFilled
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { toISTDate } from '@Apis/constants';
import closeIcon from '@Images/icons/icon-close-blue.png';
import ScheduleCronForm from './ScheduleCronForm';
import './style.scss';
import PRZSelect from '../../../../../../Common/UI/PRZSelect';

const { Option } = Select;

const UnicommerceConfiguration = ({
  user, tenants, getTenants, setStep, getTenantsLoading, getUnicommerceConnections, 
  updateUnicommerceConfiguration, unicommerceConnections, updateUnicommerceConfigurationLoading,
  deleteUcSync, deleteUcSyncLoading,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState('');
  const [defaultShelfCode, setDefaultShelfCode] = useState('DEFAULT');
  const [returnStatus, setReturnStatus] = useState('');
  const [isInventoryDeductionSyncAtShipping, setIsInventoryDeductionSyncAtShipping] = useState('');
  const [isInventoryInwardSyncOnReturn, setIsInventoryInwardSyncOnReturn] = useState('');
  const [isUnicommerceSync, setIsUnicommerceSync] = useState('');
  const [enableContinuousSync, setEnableContinuousSync] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [showJobDrawer, setShowJobDrawer] = useState(false);
  const [call, setCall] = useState(false);

  useEffect(() => {
    getTenants(1, '', true, user?.tenant_info?.org_id);
    if (unicommerceConnections) {
      setSelectedTenantIds(unicommerceConnections?.data?.configurations?.default_tenant_id);
      setDefaultShelfCode(unicommerceConnections?.data?.configurations?.default_shelf_code);
      setReturnStatus(unicommerceConnections?.data?.configurations?.confirmed_status_for_return);
      setIsInventoryDeductionSyncAtShipping(unicommerceConnections?.data?.configurations?.inventory_deduction_at_sync_shipping_packages);
      setIsInventoryInwardSyncOnReturn(unicommerceConnections?.data?.configurations?.inventory_inward_at_sync_returns);
      setIsUnicommerceSync(unicommerceConnections?.data?.configurations?.is_unicommerce_sync);
      setScheduledJobs(unicommerceConnections?.data?.unicommerce_scheduled_jobs?.map((i) => ({
        ...i,
        key: uuidv4(),
      })));
      setEnableContinuousSync(unicommerceConnections?.data?.unicommerce_scheduled_jobs?.length > 0 ? true : false);
    }
  }, []);


  const handleSubmit = () => {
    setFormSubmitted(true);

    const payload = {
      default_tenant_id: selectedTenantIds,
      default_shelf_code: defaultShelfCode,
      inventory_deduction_at_sync_shipping_packages: isInventoryDeductionSyncAtShipping,
      inventory_inward_at_sync_returns: isInventoryInwardSyncOnReturn,
      confirmed_status_for_return: returnStatus,
      is_unicommerce_sync: isUnicommerceSync,
    };

    updateUnicommerceConfiguration(payload, () => {
      setStep(5);
    });
  };
  
  const returnStatusOptions = [
    { label: 'Returned', value: 'RETURNED' },
    { label: 'Return Expected', value: 'RETURN_EXPECTED' },
  ];

  const columns = [
    {
      title: 'Business Unit Name',
      render: (record) => (
        <div>
          <TenantSelector
            selectedTenant={record?.data?.tenant_id}
            noDropdownAlign
            showSearch
            hideTitle
            customStyle={{
              border: (formSubmitted && record?.tenantIds?.length === 0)
                ? '1px solid red'
                : 'none',
              width: '100%',
              fontSize: '13px',
            }}
            containerClassName="create-price-list__input-row__input"
            placeholder="Select Business Unit"
            isMultiple
            disabled
          />

        </div>
      ),
    },
    {
      title: 'Inventory Types',
      render: (record) => (
        <Fragment>
          {
            record?.data?.inventory_types?.map((i) => (
              <div className="status-tag" style={{ backgroundColor: Helpers.getStatusColor(i === 'GOOD_INVENTORY' ? 'COMPLETED' : 'CANCELLED').color, marginRight: '5px' }}>
                {
                  i === 'GOOD_INVENTORY' ? 'Good' : 'Bad'
                }
              </div>
            ))
          }
        </Fragment>
      ),
    },
    {
      title: 'Scheduled Time',
      render: (record) => record?.cron_expression_readable,
    },
    {
      title: 'Product Group',
      render: (record) => record?.sku_list_info?.list_name,
    },
    {
      title: 'Last Sync',
      render: (record) => (
        <Fragment>
          {toISTDate(record?.last_run_at)?.format('DD MMM, YY hh:mm A')}
        </Fragment>
      ),
    },
    {
      title: '',
      render: (record) => (
        <div>
          <Popconfirm
                placement="topRight"
                title="Are you sure you want to delete this line?"
                onConfirm={() => deleteUcSync({
                  node_cron_job_ids: [record?.node_cron_job_id]
                }, () => {
                  getUnicommerceConnections(user?.tenant_info?.org_id);
                })}
                okText="Yes"
                cancelText="No"
              >
                <div className="deleteButton">
                  <DeleteOutlined />
                </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="integration-step__wrapper integration-step__mini-dropdown">
        <div style={{ fontSize: '14px', marginBottom: '5px', marginTop: '10px' }}>
          Data import configuration
        </div>

        <div className="ant-row">
          <div className="ant-col-md-24">
            <div className="flex-display flex-align-c">
              <div className="integration-step-input__wrapper">
                <PRZSelect
                  value={selectedTenantIds}
                  filterOption={false}
                  disabled={updateUnicommerceConfigurationLoading}
                  loading={getTenantsLoading}
                  placeholder="Select Default Location"
                  onChange={(value) => { setSelectedTenantIds(value); }}
                  style={{
                    border: 'none',
                    borderBottom: (formSubmitted && !selectedTenantIds) ? '1px solid rgba(255, 99, 71, 0.5) ' : '1px solid rgba(68, 130, 218, 0.2)',
                    width: '180px',
                    marginRight: '10px',
                  }}
                  showSearch
                  className="integration-step-input__input"
                >
                  {tenants?.data?.filter((item) => item?.unicommerce_facility_code)?.map((tenant) => (
                    <Option key={tenant?.tenant_id} value={tenant?.tenant_id}>
                      {tenant?.tenant_name}
                    </Option>
                  ))}
                </PRZSelect>
              </div>
              <div style={{ fontSize: '13px' }}>
                Default Procuzy location where all your Unicommerce orders will be created
                <span className="input-error">*</span>
              </div>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div className="flex-display flex-align-c">
              <div className="integration-step-input__wrapper">
                <input
                  value={defaultShelfCode}
                  placeholder="Enter Default Shelf Code..."
                  onChange={(event) => {
                    setDefaultShelfCode(event.target.value);
                  }}
                  style={{
                    border: 'none',
                    borderBottom: (formSubmitted && !selectedTenantIds) ? '1px solid rgba(255, 99, 71, 0.5) ' : '1px solid rgba(45, 125, 247, 0.25)',
                    height: '28px',
                    width: '180px',
                    fontSize: '13px',
                    marginRight: '10px',
                    paddingLeft: '10px',
                  }}
                  className="integration-step-input__input"
                />
              </div>
              <div style={{ fontSize: '13px' }}>
                Default shelf code for updating stock in Unicommerce
                <span className="input-error">*</span>
              </div>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div className="flex-display flex-align-c">
              <div className="integration-step-input__wrapper">
                <PRZSelect
                  value={returnStatus}
                  filterOption={false}
                  disabled={updateUnicommerceConfigurationLoading}
                  placeholder="Select Return Status"
                  onChange={(value) => { setReturnStatus(value); }}
                  style={{
                    border: 'none',
                    borderBottom: (formSubmitted && !selectedTenantIds) ? '1px solid rgba(255, 99, 71, 0.5) ' : '1px solid rgba(68, 130, 218, 0.2)',
                    width: '180px',
                  }}
                  showSearch
                  className="integration-step-input__input"
                  options={returnStatusOptions}
                />
              </div>
              <div style={{ fontSize: '13px' }}>
                Status on which return should be created in Procuzy
                <span className="input-error">*</span>
              </div>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div style={{ marginTop: '10px', fontWeight: '400', fontSize: '12px' }}>
              <Checkbox
                checked={isUnicommerceSync}
                onChange={(e) => {
                  setIsUnicommerceSync(e.target.checked);
                }}
                disabled={updateUnicommerceConfigurationLoading}
              >
                Enable order sync from unicommerce
                {' '}
              </Checkbox>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div style={{ marginTop: '10px', fontWeight: '400', fontSize: '12px' }}>
              <Checkbox
                checked={isInventoryDeductionSyncAtShipping}
                onChange={(e) => {
                  setIsInventoryDeductionSyncAtShipping(e.target.checked);
                }}
                disabled={updateUnicommerceConfigurationLoading}
              >
                Deduct stock when invoices are created
                {' '}
              </Checkbox>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div style={{ marginTop: '10px', fontWeight: '400', fontSize: '12px' }}>
              <Checkbox
                checked={isInventoryInwardSyncOnReturn}
                onChange={(e) => {
                  setIsInventoryInwardSyncOnReturn(e.target.checked);
                }}
                disabled={updateUnicommerceConfigurationLoading}
              >
                Update stock when returns are created
                {' '}
              </Checkbox>
            </div>
          </div>
          <div className="ant-col-md-24">
            <div style={{ marginTop: '10px', fontWeight: '400', fontSize: '12px' }}>
              <Checkbox
                checked={enableContinuousSync}
                onChange={(e) => {
                  setEnableContinuousSync(e.target.checked);
                }}
                disabled={updateUnicommerceConfigurationLoading || scheduledJobs?.length > 0}
              >
                Enable continuous sync of stock levels from Procuzy to Unicommerce
                {' '}
              </Checkbox>
            </div>
          </div>
          {
            enableContinuousSync && <div className="ant-col-md-24" style={{ marginTop: '10px' }}>
              <Table
                  columns={columns}
                  loading={updateUnicommerceConfigurationLoading || deleteUcSyncLoading}
                  dataSource={scheduledJobs}
                  size="small"
                  pagination={false}
                />
                <div className="new-row-button" onClick={() => setShowJobDrawer(true)} style={{ maxWidth: '110px !important' }}>
                  <span className="new-row-button__icon"><PlusCircleFilled /></span>
                  <div>New Item</div>
                </div>
            </div>
          }
        </div>

        <Drawer
          open={showJobDrawer}
          width="360px"
          mask
          onClose={() => {
            setShowJobDrawer(false);
          }}
          destroyOnClose
        >
          <div className="custom-drawer__header-wrapper">
            <div className="custom-drawer__header" style={{ width: '315px' }}>
              <H3Text text="Schedule stock sync" className="custom-drawer__title" />
              <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowJobDrawer(false)} />
            </div>
          </div>
          <ScheduleCronForm callback={() => {
            setShowJobDrawer(false);
            getUnicommerceConnections(user?.tenant_info?.org_id);
          }}/>
        </Drawer>
        <div className="integration-step__footer" style={{ display: 'flex', paddingTop: '10px', paddingLeft: '15px' }}>
            <Button
              onClick={() => setStep(3)}
              disabled={updateUnicommerceConfigurationLoading}
            >
                Back
            </Button>
            &nbsp;&nbsp;
            <Button
              type="primary"
              onClick={() => handleSubmit()}
              loading={updateUnicommerceConfigurationLoading}
              disabled={updateUnicommerceConfigurationLoading}
            >
              Next Step
            </Button>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({ /* Import Reducers here */ UserReducers, UnicommerceIntegrationReducers, TenantReducers }) => ({
  user: UserReducers.user,
  getTenantsLoading: TenantReducers.getTenantsLoading,
  tenants: TenantReducers.tenants,
  UnicommerceConnections: UnicommerceIntegrationReducers.UnicommerceConnections,
  updateUnicommerceConfigurationLoading: UnicommerceIntegrationReducers.updateUnicommerceConfigurationLoading,
  deleteUcSyncLoading: UnicommerceIntegrationReducers.deleteUcSyncLoading,
  unicommerceConnections: UnicommerceIntegrationReducers.unicommerceConnections,
});

const mapDispatchToProps = (/* Import actions here */ dispatch) => ({
  getTenants: (page, keyword, isVerified, orgId) => dispatch(TenantActions.getTenants(page, keyword, isVerified, orgId)),
  getUnicommerceConnections: (orgId, callback) => dispatch(UnicommerceIntegrationActions.getUnicommerceConnections(orgId, callback)),
  updateUnicommerceConfiguration: (payload, callback) => dispatch(UnicommerceIntegrationActions.updateUnicommerceConfiguration(payload, callback)),
  deleteUcSync: (payload, callback) => dispatch(UnicommerceIntegrationActions.deleteUcSync(payload, callback)),
});

UnicommerceConfiguration.propTypes = {
  user: PropTypes.any,
  tenants: PropTypes.any,
  getTenantsLoading: PropTypes.bool,
  unicommerceConnections: PropTypes.any,
  getTenants: PropTypes.func,
  setStep: PropTypes.func,
  updateUnicommerceConfiguration: PropTypes.func,
  updateUnicommerceConfigurationLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(UnicommerceConfiguration));
